import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';

interface Project {
  id: number;
  name: string;
  status: string;
  progress: number;
  tasks: any[];
}

export default function RecentProjectsSidebar({ projects }: { projects: Project[] }) {
  const theme = useTheme();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
      case 'In Progress':
        return 'primary';
      case 'Completed':
        return 'success';
      case 'On Hold':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', borderRadius: 2 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Recent Projects
        </Typography>
        <Stack spacing={2}>
          {projects.slice(0, 3).map((project) => (
            <Box 
              key={project.id}
              sx={{ 
                p: 1.5, 
                borderRadius: 1, 
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, flex: 1, mr: 1 }}>
                  {project.name}
                </Typography>
                <Chip 
                  label={project.status} 
                  size="small" 
                  color={getStatusColor(project.status) as any} 
                  variant="outlined" 
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Box>
              
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {Math.round(project.progress)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={project.progress} 
                  sx={{ height: 4, borderRadius: 2 }} 
                />
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}>
                {project.tasks.length} tasks recorded
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
